import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "./PageHeader";
import { doctors } from "./Doctors";
import "./DoctorDetails.css";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";

const DoctorDetails = () => {
  useEffect(() => {
    AOS.init({ duration: 4000, once: true });
  }, []);

  const { id } = useParams();
  const doctor = doctors.find((d) => d.id === parseInt(id));

  if (!doctor) return <p>Doctor not found</p>;

  const details = {
    1: {
      education: [
        "MBBS - Dr. V. M. Govt Medical College, Solapur (MH) - 2013-2019",
        "MS - B.J. Govt medical College and Sassoon General Hospital,Pune.",
        "Fellowship in Minimal Access Surgery (New Delhi)",
        "Fellowship in Gastrointestinal Endoscopy",
        "Training in Advanced Laparoscopy & Endoscopy – Cleveland Clinic, Ohio (USA)",
      ],
      memberships: [
        "Indian Association of Gastrointestinal Endosurgeons",
        "Association of Surgeons of India",
        "Society of Gastrointestinal Endoscopist of India",
      ],
      expertise: [
        "General Surgery: Hernia, Hydrocele, Appendix, Circumcision, Cysts, Lipoma, Swellings, Intestine & Stomach, Piles, Fissure, Fistula, Pilonidal Sinus",
        "Laparoscopic Surgery: Hernia, Gall Bladder Stone, Varicocele, Appendix",
        "Gastrointestinal Endoscopy: Therapeutic & Diagnostic Endoscopy, Variceal Banding, Biopsy, Sclerotherapy, Acidity",
      ],
    },

    2: {
      education: [
        "MBBS - Dr. VM Govt Medical College, Solapur (2013–2019)",
        "MS - TNMC & BYL Nair Charitable Hospital, Mumbai (2019–2022)",
        "FMAS - Fellowship in Minimal Access Surgery, World Laparoscopy Hospital, New Delhi",
        "F.ART - Fellowship in Assisted Reproductive Technology",
        "Perceptorship - Urogynaecology, Cleveland Clinic, USA",
        "Sonography Training (Ob & GYN) - FMF UK",
      ],

      memberships: ["Member of Pune Obstetrics & Gynaecology Society (POAS)"],

      expertise: [
        "High-Risk Obstetrics: RPL, PIH, GDM, Rh Negative Pregnancy, Anaemia",
        "Adolescent & Sexual Health",
        "Laparoscopic Surgery: TLH, Ovarian Cystectomy, Ectopic Pregnancy Removal, Myomectomy, Family Planning",
        "Gynaecological Disorders: Prolapse Surgery, Stress Urinary Incontinence",
        "Infertility & ART Procedures: IUI, IVF, ICSI, Hysteroscopy",
      ],
    },
  };

  const docDetails = details[doctor.id];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>
          {doctor.name} | {doctor.specialization} | Dr. Chavan Multispeciality Hospital Indapur
        </title>

        <meta
          name="description"
          content={`${doctor.name} is a specialist in ${doctor.specialization} at Dr. Chavan Multispeciality Hospital, Indapur. View education, expertise, memberships, and treatments offered.`}
        />

        <meta
          name="keywords"
          content={`${
            doctor.name
          }, ${doctor.specialization}, doctor in Indapur, best ${doctor.specialization.toLowerCase()} in Indapur, Dr Chavan Hospital`}
        />

        <link
          rel="canonical"
          href={`https://drchavanhospital.in/doctors/${doctor.id}`}
        />

        <meta property="og:title" content={doctor.name} />
        <meta
          property="og:description"
          content={`${doctor.name} – ${doctor.specialization} at Dr. Chavan Multispeciality Hospital Indapur.`}
        />
        <meta property="og:image" content={doctor.detailImage || doctor.image} />
        <meta
          property="og:url"
          content={`https://drchavanhospital.in/doctors/${doctor.id}`}
        />
        <meta property="og:type" content="profile" />

        {/* Doctor Schema (Structured Data) */}
        <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Physician",
          "name": "${doctor.name}",
          "image": "${doctor.detailImage || doctor.image}",
          "medicalSpecialty": "${doctor.specialization}",
          "affiliation": {
            "@type": "Hospital",
            "name": "Dr. Chavan Multispeciality Hospital",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Indapur",
              "addressRegion": "Maharashtra",
              "addressCountry": "India"
            }
          },
          "alumniOf": ["${doctor.qualifications}"],
          "url": "https://drchavanhospital.in/doctors/${doctor.id}"
        }
      `}</script>
      </Helmet>

      <PageHeader
        title={doctor.name}
        breadcrumbs={["Home", "Doctors", doctor.name]}
        background="/images/pg.webp"
      />

      {/* Hidden H1 for SEO */}
      <h1 style={{ display: "none" }}>
        {doctor.name} – {doctor.specialization} in Indapur
      </h1>

      <div className="doctor-details-page">
        <div className="doctor-profile">
          {/* IMAGE */}
          <div className="doctor-image" data-aos="fade-right">
            <img
              src={doctor.detailImage || doctor.image}
              alt={`${doctor.name} – ${doctor.specialization} in Indapur`}
            />
          </div>

          {/* INFO */}
          <div className="doctor-info" data-aos="fade-left">
            <h2>{doctor.name}</h2>
            <p className="qualifications">{doctor.qualifications}</p>
            <p className="specialization">{doctor.specialization}</p>

            <h3>Education & Training</h3>
            <ul>
              {docDetails.education.map((edu, index) => (
                <li key={index}>{edu}</li>
              ))}
            </ul>

            <h3>Memberships</h3>
            <ul>
              {docDetails.memberships.map((mem, index) => (
                <li key={index}>{mem}</li>
              ))}
            </ul>

            <h3>Areas of Expertise</h3>

            {Array.isArray(docDetails.expertise) && (
              <ul>
                {docDetails.expertise.map((exp, index) => (
                  <li key={index}>{exp}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DoctorDetails;
